import { useEffect, useState } from 'react';
import type { AuthState, ClientPrincipal, OscarRole } from '@/types/oscar';

/**
 * useAuth — single source of truth for who the signed-in user is and what
 * roles they hold on the Oscar tenant.
 *
 * Reads /.auth/me (served by the SWA, not our Functions), caches the
 * result for the lifetime of the component tree, and exposes a typed
 * AuthState.
 *
 * The SWA stamps roles onto the principal as `userRoles` after calling our
 * /api/GetRoles function. Role checks here are client-side UX only — the
 * API enforces server-side via AuthHelper.IsInRole / CanMutate / CanRead.
 *
 * `displayName` is derived from the principal's claims (the AAD `name`
 * claim, or givenName + surname as a fallback).
 */

interface AuthMeResponse {
  clientPrincipal: ClientPrincipal | null;
}

const initialState: AuthState = {
  user: null,
  roles: [],
  isAuthenticated: false,
  isAdmin: false,
  isHelpdesk: false,
  isFinance: false,
  isReviewer: false,
  isExternal: false,
  canRead: false,
  canMutate: false,
  canExportFinance: false,
  canUseOnPass: false,
  isAssetManager: false,
  canApproveHardware: false,
  isItFinanceApprover: false,
  canApprovePurchase: false,
  displayName: null,
  loading: true,
};

function deriveDisplayName(principal: ClientPrincipal | null): string | null {
  if (!principal) return null;
  const claims = principal.claims ?? [];
  const lookup = (predicate: (typ: string) => boolean): string | null => {
    const hit = claims.find((c) => typeof c.typ === 'string' && predicate(c.typ));
    return hit?.val?.trim() ? hit.val.trim() : null;
  };

  const name = lookup(
    (t) => t === 'name' || t === 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
  );
  if (name) return name;

  const given = lookup((t) => t === 'given_name' || t.endsWith('/givenname'));
  const family = lookup((t) => t === 'family_name' || t.endsWith('/surname'));
  if (given && family) return `${given} ${family}`;
  if (given) return given;

  return null;
}

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>(initialState);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetch('/.auth/me', {
          credentials: 'include',
          signal: controller.signal,
        });
        if (!response.ok) {
          setState({ ...initialState, loading: false });
          return;
        }
        const data = (await response.json()) as AuthMeResponse;
        const user = data.clientPrincipal;
        if (!user) {
          setState({ ...initialState, loading: false });
          return;
        }
        const roles = user.userRoles ?? [];
        const isAdmin = roles.includes('oscar-admin');
        const isHelpdesk = roles.includes('oscar-helpdesk');
        const isFinance = roles.includes('oscar-finance');
        const isReviewer = roles.includes('oscar-reviewer');
        const isExternal = roles.includes('oscar-external');
        const isAssetManager = roles.includes('oscar-asset-manager');
        const isItFinanceApprover = roles.includes('oscar-it-finance-approver');
        const isOnPassUser = roles.includes('onpass-user');
        setState({
          user,
          roles,
          isAuthenticated: true,
          isAdmin,
          isHelpdesk,
          isFinance,
          isReviewer,
          // ⛔ NOT in canRead below: an external user reads nothing until a page admits the role.
          isExternal,
          canRead: isAdmin || isHelpdesk || isFinance || isReviewer,
          canMutate: isAdmin || isHelpdesk,
          canExportFinance: isFinance || isAdmin,
          // ⛔ Deciding a hardware request is a commitment to SPEND, so it is
          // narrower than canMutate: helpdesk can see the registry (they fulfil
          // the work) and cannot approve. Mirrors AuthHelper.CanApproveHardware,
          // which is the authoritative check.
          isAssetManager,
          canApproveHardware: isAssetManager || isAdmin,
          // The SPEND is a second person's decision: NOT canApproveHardware.
          // Mirrors AuthHelper.CanApprovePurchase, which is the authoritative check.
          isItFinanceApprover,
          canApprovePurchase: isItFinanceApprover || isAdmin,
          canUseOnPass: isOnPassUser || isAdmin || isHelpdesk,
          displayName: deriveDisplayName(user),
          loading: false,
        });
      } catch (err) {
        if ((err as Error).name === 'AbortError') return;
        setState({ ...initialState, loading: false });
      }
    })();

    return () => controller.abort();
  }, []);

  return state;
}

/** Convenience: does the current auth state include the given role? */
export function hasRole(state: AuthState, role: OscarRole): boolean {
  return state.roles.includes(role);
}
